import React from 'react';
import { 
  Users, Film, MessageSquare, DollarSign, 
  Check, Trash2, TrendingUp, Flag 
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    electric: 'text-electric bg-electric/20',
    pulse: 'text-pulse bg-pulse/20',
    green: 'text-green-400 bg-green-400/20',
    amber: 'text-amber-400 bg-amber-400/20',
    red: 'text-red-400 bg-red-400/20',
    purple: 'text-purple-400 bg-purple-400/20',
    cyan: 'text-cyan-400 bg-cyan-400/20',
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-4">
      <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-3`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
};

const DashboardOverview = ({ 
  stats, 
  users, 
  comments, 
  onApproveComment, 
  onDeleteComment 
}) => {
  if (!stats) return null;

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={stats.total_users} color="electric" />
        <StatCard icon={Film} label="Total Content" value={stats.total_content} color="pulse" />
        <StatCard icon={MessageSquare} label="Comments" value={stats.total_comments} color="green" />
        <StatCard icon={DollarSign} label="Premium Users" value={stats.premium_users} color="amber" />
        <StatCard icon={Flag} label="Flagged Comments" value={stats.flagged_comments} color="red" />
        <StatCard icon={DollarSign} label="PPV Purchases" value={stats.ppv_purchases} color="purple" />
        <StatCard icon={TrendingUp} label="New This Week" value={stats.recent_signups} color="cyan" />
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Users className="h-5 w-5 text-electric" />
            <span>Recent Users</span>
          </h3>
          <div className="space-y-3">
            {users.slice(0, 5).map((user) => (
              <div key={user.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-foreground font-medium">{user.username}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
                {user.is_premium && (
                  <span className="px-2 py-1 bg-electric/20 text-electric text-xs rounded-full">Premium</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Flagged Comments */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Flag className="h-5 w-5 text-red-500" />
            <span>Flagged Comments</span>
          </h3>
          <div className="space-y-3">
            {comments.slice(0, 5).map((comment) => (
              <div key={comment.id} className="p-3 bg-white/5 rounded-lg">
                <p className="text-foreground text-sm line-clamp-2">{comment.text}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-muted-foreground">by {comment.username}</p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onApproveComment(comment.id)}
                      className="p-1 text-green-500 hover:bg-green-500/20 rounded"
                      data-testid={`approve-comment-${comment.id}`}
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDeleteComment(comment.id)}
                      className="p-1 text-red-500 hover:bg-red-500/20 rounded"
                      data-testid={`delete-comment-${comment.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {comments.length === 0 && (
              <p className="text-muted-foreground text-center py-4">No flagged comments</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
